import { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? (API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`)
  : "/api";

const EMP_TYPE_OPTIONS = [
  { value: "T", label: "Teaching" },
  { value: "N", label: "Non-Teaching" },
];

const JOB_TYPE_OPTIONS = [
  { value: "P", label: "Permanent" },
  { value: "C", label: "Contractual" },
  { value: "D", label: "Daily Wages" },
  { value: "M", label: "MTS" },
  { value: "T", label: "Temporary Basis" },
  { value: "O", label: "Out Sourced" },
];

const JOB_TYPE_MAP = {
  P: "Permanent",
  T: "Temporary Basis",
  C: "Contractual",
  D: "Daily Wages",
  M: "MTS",
  O: "Out Sourced",
};

const SERVICE_TYPE_MAP = {
  N: "NonTeaching",
  T: "Teaching",
};

function formatDate(value) {
  if (!value || value === "0000-00-00") {
    return "";
  }

  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) {
    return "";
  }

  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();
  return `${day}/${month}/${year}`;
}

export default function SeniorityListOffice() {
  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = Number.parseInt(localUser?.moduleId, 10);

  const [designations, setDesignations] = useState([]);
  const [designationID, setDesignationID] = useState("1000");
  const [empType, setEmpType] = useState("T");
  const [jobType, setJobType] = useState("P");

  const [rows, setRows] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (moduleId !== 4) return;
    fetch(`${API_BASE}/meta/designations`)
      .then((res) => res.json())
      .then((data) => setDesignations(Array.isArray(data) ? data : []))
      .catch(() => setDesignations([]));
  }, [moduleId]);

  const onView = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams({
        moduleId: String(moduleId),
        designationID,
        empType,
        jobType,
      });
      const res = await fetch(`${API_BASE}/employees/seniority-list-office?${params.toString()}`);
      const payload = await res.json();
      if (!res.ok) throw new Error(payload?.message || "Failed to fetch seniority list");
      setRows(Array.isArray(payload?.data) ? payload.data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch seniority list");
    } finally {
      setLoading(false);
    }
  };

  if (moduleId !== 4) {
    return (
      <section className="container-fluid">
        <div className="alert alert-danger mt-3">Unauthorized access Or Session Timed out.</div>
      </section>
    );
  }

  return (
    <section className="container-fluid">
      <h3 className="mb-3">Seniority List</h3>

      <form className="row g-2 align-items-end mb-3" onSubmit={onView}>
        <div className="col-sm-3">
          <label className="form-label mb-1">Designation</label>
          <select className="form-select form-select-sm" value={designationID} onChange={(e) => setDesignationID(e.target.value)}>
            <option value="1000">ALL</option>
            {designations.map((d) => (
              <option key={d.DesignationID} value={d.DesignationID}>{d.Designation}</option>
            ))}
          </select>
        </div>

        <div className="col-sm-2">
          <label className="form-label mb-1">Emp Type</label>
          <select className="form-select form-select-sm" value={empType} onChange={(e) => setEmpType(e.target.value)}>
            {EMP_TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="col-sm-2">
          <label className="form-label mb-1">Job Type</label>
          <select className="form-select form-select-sm" value={jobType} onChange={(e) => setJobType(e.target.value)}>
            {JOB_TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="col-sm-2">
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      {hasSearched && (
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>Name</th>
                <th>Gender</th>
                <th>Father&apos;s Name</th>
                <th>Mobile</th>
                <th>PF</th>
                <th>Merit</th>
                <th>DOJ</th>
                <th>DOC</th>
                <th>DOB</th>
                <th>Valid Upto</th>
                <th>Designation</th>
                <th>Department</th>
                <th>Job Type</th>
                <th>Service Type</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={15} className="text-center text-muted">No records found.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={15} className="text-center text-muted">Loading...</td>
                </tr>
              )}
              {rows.map((row, index) => (
                <tr key={`${row.EmployeeID}-${index}`}>
                  <td>{index + 1}</td>
                  <td>{row.EmpName || ""}</td>
                  <td>{row.Gender || ""}</td>
                  <td>{row.FatherName || ""}</td>
                  <td>{row.Mobile || ""}</td>
                  <td>{row.PFNo || ""}</td>
                  <td>{row.MeritRank || ""}</td>
                  <td>{formatDate(row.DOJ)}</td>
                  <td>{formatDate(row.DOC)}</td>
                  <td>{formatDate(row.DOB)}</td>
                  <td>{formatDate(row.DOR)}</td>
                  <td>{row.Designation || ""}</td>
                  <td>{row.Department || ""}</td>
                  <td>{JOB_TYPE_MAP[row.JobType] || row.JobType || ""}</td>
                  <td>{SERVICE_TYPE_MAP[row.EmpType] || row.EmpType || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
